import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../store/authContext';
import {
  createHelpArticle,
  getHelpContent,
  getHelpTickets,
  updateHelpCategory,
  updateHelpArticle,
} from '../../api/helpCenter';

const { Title } = Typography;
const statusMeta = {
  OPEN: { label: '待处理', color: 'orange' },
  IN_PROGRESS: { label: '处理中', color: 'blue' },
  WAITING_USER: { label: '等待用户', color: 'purple' },
  RESOLVED: { label: '已解决', color: 'green' },
  CLOSED: { label: '已关闭', color: 'default' },
};

const HelpCenter = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { admin } = useAuth();
  const canEdit = admin?.role === 'SUPER_ADMIN';
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'tickets');
  const [content, setContent] = useState({ categories: [] });
  const [contentLoading, setContentLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketPage, setTicketPage] = useState({ current: Number(searchParams.get('page')) || 1, pageSize: Number(searchParams.get('size')) || 20, total: 0 });
  const ticketCurrent = ticketPage.current;
  const ticketPageSize = ticketPage.pageSize;
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [ticketUserId, setTicketUserId] = useState(searchParams.get('userId') || '');
  const [ticketCategory, setTicketCategory] = useState(searchParams.get('category') || '');
  const [ticketRange, setTicketRange] = useState(() => {
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    return from && to ? [dayjs(from), dayjs(to)] : [];
  });
  const [articleSearch, setArticleSearch] = useState('');
  const [articleModal, setArticleModal] = useState(null);
  const [categoryModal, setCategoryModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [articleForm] = Form.useForm();
  const [categoryForm] = Form.useForm();

  const loadContent = useCallback(async () => {
    setContentLoading(true);
    try {
      setContent(await getHelpContent());
    } finally {
      setContentLoading(false);
    }
  }, []);

  const loadTickets = useCallback(async (page = 1, size = 20) => {
    setTicketLoading(true);
    try {
      const result = await getHelpTickets({
        page,
        size,
        status: statusFilter || undefined,
        userId: ticketUserId || undefined,
        categoryCode: ticketCategory || undefined,
        from: ticketRange[0]?.toISOString(),
        to: ticketRange[1]?.toISOString(),
      });
      setTickets(result.list || []);
      setTicketPage({ current: result.page || page, pageSize: result.size || size, total: result.total || 0 });
    } finally {
      setTicketLoading(false);
    }
  }, [statusFilter, ticketUserId, ticketCategory, ticketRange]);

  useEffect(() => { loadContent(); }, [loadContent]);
  useEffect(() => {
    if (activeTab === 'tickets') loadTickets(ticketCurrent, ticketPageSize);
  }, [activeTab, loadTickets, ticketCurrent, ticketPageSize]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (activeTab !== 'tickets') next.set('tab', activeTab);
    if (ticketCurrent > 1) next.set('page', String(ticketCurrent));
    if (ticketPageSize !== 20) next.set('size', String(ticketPageSize));
    if (statusFilter) next.set('status', statusFilter);
    if (ticketUserId) next.set('userId', ticketUserId);
    if (ticketCategory) next.set('category', ticketCategory);
    if (ticketRange[0] && ticketRange[1]) {
      next.set('from', ticketRange[0].toISOString());
      next.set('to', ticketRange[1].toISOString());
    }
    setSearchParams(next, { replace: true });
  }, [activeTab, setSearchParams, statusFilter, ticketCategory, ticketCurrent, ticketPageSize, ticketRange, ticketUserId]);

  const articles = useMemo(() => content.categories.flatMap((category) => (
    (category.articles || []).map((article) => ({ ...article, categoryId: category.id, categoryTitle: category.title }))
  )), [content]);
  const filteredArticles = useMemo(() => {
    const query = articleSearch.trim().toLowerCase();
    if (!query) return articles;
    return articles.filter((article) => [article.question, article.answer, article.keywords].join(' ').toLowerCase().includes(query));
  }, [articleSearch, articles]);

  const openArticle = (article) => {
    setArticleModal(article || { categoryId: content.categories[0]?.id, status: 'DRAFT', sortOrder: 0, version: 1 });
    articleForm.setFieldsValue(article || { categoryId: content.categories[0]?.id, status: 'DRAFT', sortOrder: 0, version: 1 });
  };

  const saveArticle = async (values) => {
    setSaving(true);
    try {
      if (articleModal?.id) await updateHelpArticle(articleModal.id, values);
      else await createHelpArticle(values);
      message.success('帮助内容已保存');
      setArticleModal(null);
      await loadContent();
    } finally {
      setSaving(false);
    }
  };

  const openCategory = (category) => {
    setCategoryModal(category);
    categoryForm.setFieldsValue(category);
  };

  const saveCategory = async (values) => {
    setSaving(true);
    try {
      await updateHelpCategory(categoryModal.id, values);
      message.success('帮助分类已保存');
      setCategoryModal(null);
      await loadContent();
    } finally {
      setSaving(false);
    }
  };

  const openTicket = (id) => {
    const query = searchParams.toString();
    navigate(`/admin/help-center/tickets/${id}${query ? `?${query}` : ''}`);
  };

  const articleColumns = [
    { title: '分类', dataIndex: 'categoryTitle', width: 150 },
    { title: '问题', dataIndex: 'question', ellipsis: true },
    { title: '状态', dataIndex: 'status', width: 100, render: (value) => <Tag color={value === 'PUBLISHED' ? 'green' : 'default'}>{value === 'PUBLISHED' ? '已发布' : '草稿'}</Tag> },
    { title: '排序', dataIndex: 'sortOrder', width: 80 },
    canEdit && { title: '操作', width: 90, render: (_, record) => <Button type="link" icon={<EditOutlined />} onClick={() => openArticle(record)}>编辑</Button> },
  ].filter(Boolean);

  const categoryColumns = [
    { title: '分类', dataIndex: 'title', width: 180 },
    { title: '编码', dataIndex: 'code', width: 180 },
    { title: '问题数', dataIndex: 'articles', width: 100, render: (items) => items?.length || 0 },
    { title: '状态', dataIndex: 'enabled', width: 100, render: (value) => <Tag color={value ? 'green' : 'default'}>{value ? '已上架' : '已下架'}</Tag> },
    { title: '排序', dataIndex: 'sortOrder', width: 80 },
    canEdit && { title: '操作', width: 90, render: (_, record) => <Button type="link" icon={<EditOutlined />} onClick={() => openCategory(record)}>编辑</Button> },
  ].filter(Boolean);

  const ticketColumns = [
    { title: '编号', dataIndex: 'id', width: 90 },
    { title: '用户', dataIndex: 'userId', width: 90 },
    { title: '标题', dataIndex: 'title', ellipsis: true },
    { title: '分类', dataIndex: 'categoryCode', width: 150 },
    { title: '状态', dataIndex: 'status', width: 110, render: (value) => <Tag color={statusMeta[value]?.color}>{statusMeta[value]?.label || value}</Tag> },
    { title: '更新时间', dataIndex: 'updatedAt', width: 180 },
    { title: '操作', width: 90, render: (_, record) => <Button type="link" onClick={() => openTicket(record.id)}>查看</Button> },
  ];

  return (
    <div>
      <Space align="center" style={{ marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>帮助中心</Title>
        <Button icon={<ReloadOutlined />} onClick={() => { loadContent(); loadTickets(); }}>刷新</Button>
      </Space>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
        { key: 'tickets', label: '反馈工单', children: (
          <Card>
            <Space style={{ marginBottom: 16 }}>
              <Select value={statusFilter} onChange={(value) => { setStatusFilter(value || ''); setTicketPage((current) => ({ ...current, current: 1 })); }} allowClear placeholder="筛选状态" style={{ width: 160 }} options={Object.entries(statusMeta).map(([value, meta]) => ({ value, label: meta.label }))} />
              <Select value={ticketCategory} onChange={(value) => { setTicketCategory(value || ''); setTicketPage((current) => ({ ...current, current: 1 })); }} allowClear placeholder="筛选分类" style={{ width: 180 }} options={content.categories.map((category) => ({ value: category.code, label: category.title }))} />
              <Input value={ticketUserId} onChange={(event) => { setTicketUserId(event.target.value.replace(/\D/g, '')); setTicketPage((current) => ({ ...current, current: 1 })); }} placeholder="用户 ID" style={{ width: 140 }} />
              <DatePicker.RangePicker value={ticketRange} onChange={(value) => { setTicketRange(value || []); setTicketPage((current) => ({ ...current, current: 1 })); }} allowClear />
            </Space>
            <Table
              rowKey="id"
              loading={ticketLoading}
              dataSource={tickets}
              columns={ticketColumns}
              onRow={(record) => ({ onClick: () => openTicket(record.id), style: { cursor: 'pointer' } })}
              pagination={{ ...ticketPage, showSizeChanger: true, onChange: (page, size) => setTicketPage((current) => ({ ...current, current: page, pageSize: size })) }}
            />
          </Card>
        ) },
        { key: 'articles', label: 'FAQ 知识库', children: (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card title="帮助分类">
              <Table rowKey="id" loading={contentLoading} dataSource={content.categories} columns={categoryColumns} pagination={false} />
            </Card>
            <Card title="FAQ 问题" extra={<Space><Input value={articleSearch} onChange={(event) => setArticleSearch(event.target.value)} placeholder="搜索问题、答案或关键词" allowClear style={{ width: 240 }} />{canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={() => openArticle()}>新增问题</Button>}</Space>}>
              <Table rowKey="id" loading={contentLoading} dataSource={filteredArticles} columns={articleColumns} pagination={false} />
            </Card>
          </Space>
        ) },
      ]} />

      <Modal title={articleModal?.id ? '编辑帮助问题' : '新增帮助问题'} open={Boolean(articleModal)} onCancel={() => setArticleModal(null)} onOk={() => articleForm.submit()} confirmLoading={saving} width={720} destroyOnClose>
        <Form form={articleForm} layout="vertical" onFinish={saveArticle} preserve={false}>
          <Form.Item name="categoryId" label="分类" rules={[{ required: true }]}><Select options={content.categories.map((category) => ({ value: category.id, label: category.title }))} /></Form.Item>
          <Form.Item name="question" label="问题" rules={[{ required: true, max: 255 }]}><Input /></Form.Item>
          <Form.Item name="answer" label="答案" rules={[{ required: true, max: 10000 }]}><Input.TextArea rows={6} /></Form.Item>
          <Space style={{ display: 'flex' }}>
            <Form.Item name="keywords" label="关键词"><Input placeholder="用逗号分隔" /></Form.Item>
            <Form.Item name="actionTarget" label="跳转目标"><Input placeholder="如 PRICE_MONITOR" /></Form.Item>
            <Form.Item name="sortOrder" label="排序"><Input type="number" /></Form.Item>
            <Form.Item name="status" label="状态"><Select options={[{ value: 'DRAFT', label: '草稿' }, { value: 'PUBLISHED', label: '已发布' }]} /></Form.Item>
            <Form.Item name="version" hidden><Input /></Form.Item>
          </Space>
        </Form>
      </Modal>

      <Modal title="编辑帮助分类" open={Boolean(categoryModal)} onCancel={() => setCategoryModal(null)} onOk={() => categoryForm.submit()} confirmLoading={saving} width={560} destroyOnClose>
        <Form form={categoryForm} layout="vertical" onFinish={saveCategory} preserve={false}>
          <Form.Item name="code" label="编码" rules={[{ required: true, max: 64 }]}><Input disabled /></Form.Item>
          <Form.Item name="title" label="名称" rules={[{ required: true, max: 80 }]}><Input /></Form.Item>
          <Form.Item name="description" label="描述"><Input.TextArea rows={3} maxLength={255} /></Form.Item>
          <Space style={{ display: 'flex' }}>
            <Form.Item name="iconKey" label="图标标识"><Input /></Form.Item>
            <Form.Item name="sortOrder" label="排序"><Input type="number" /></Form.Item>
            <Form.Item name="enabled" label="状态"><Select options={[{ value: true, label: '已上架' }, { value: false, label: '已下架' }]} /></Form.Item>
            <Form.Item name="version" hidden><Input /></Form.Item>
          </Space>
        </Form>
      </Modal>

    </div>
  );
};

export default HelpCenter;
